import React, { useEffect, useState } from "react";
import { IBuyGiftCardModel } from "../../../../models/buy-giftcard/BuyGiftCard.model";
import { IReviewAndPayPaymentStepModel, ReviewAndPayPaymentStepModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPayPaymentStep.model";
import { IReviewAndPaySenderInfoStepModel, ReviewAndPaySenderInfoStepModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPaySenderInfoStep.model";
import { IReviewAndPaySubpageModel, ReviewAndPaySubpageModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPaySubpage.model";
import { IReviewAndPayWhenToSendStepModel, ReviewAndPayWhenToSendStepModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPayWhenToSendStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { BuyGiftCardSubpageEnum } from "../../../../models/enums/BuyGiftCardSubpageEnum";
import { IBuyGiftCardSubpageProps } from "../../../../models/interfaces/BuyGiftCardSubpage.interface";
import ReviewAndPayPaymentStep from "../steps/ReviewAndPayPaymentStep";
import ReviewAndPayReviewStep, { IReviewAndPayReviewStepModel } from "../steps/ReviewAndPayReviewStep";
import ReviewAndPaySenderInfoStep from "../steps/ReviewAndPaySenderInfoStep";
import ReviewAndPayWhenToSendStep from "../steps/ReviewAndPayWhenToSendStep";

interface IReviewAndPaySubpageProps extends IBuyGiftCardSubpageProps {
    subpageModel: IReviewAndPaySubpageModel;
    reviewModel: IReviewAndPayReviewStepModel;
    hasToSaveBasket: boolean;

    getPageModel(): IBuyGiftCardModel;
    handleError(message: string): void;
    handleSenderInfoSubmit(model: IReviewAndPaySenderInfoStepModel): void;
    handleWhenToSendSubmit(model: IReviewAndPayWhenToSendStepModel): void;
    handleSubpageSubmit(model: IReviewAndPaySubpageModel): void;
}

const ReviewAndPaySubpage = (props: IReviewAndPaySubpageProps) => {
    //#region STATE PROPERTIES

    const [subpageModel, setSubpageModel] = useState<IReviewAndPaySubpageModel>(props.subpageModel);
    const [isSaving, setIsSaving] = useState(false);
    const [stepsStatus, setStepsStatus] = useState({
        senderInfo: ReviewAndPaySenderInfoStepModel.isValid(props.subpageModel.reviewAndPaySenderInfo, null),
        whenToSend: ReviewAndPayWhenToSendStepModel.isValid(props.subpageModel.reviewAndPayWhenToSend),
        paymentStep: false
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (subpageModel) {
            setTimeout(() => {
                setIsSaving(false);
            }, 500);
        }
    }, [subpageModel]);

    useEffect(() => {
        if (stepsStatus.senderInfo && stepsStatus.whenToSend && stepsStatus.paymentStep) {
            if (ReviewAndPaySubpageModel.isValid(subpageModel)) {
                props.handleSubpageSubmit(subpageModel);
            } else {
                console.error('Invalid reviewAndPay subpage model');
                console.error(subpageModel);
            }
        }
    }, [stepsStatus]);

    //#endregion

    //#region SENDER INFO STEP

    const handleSenderInfoSubmit = (model: IReviewAndPaySenderInfoStepModel): void => {
        const isModelValid = ReviewAndPaySenderInfoStepModel.isValid(model, null);

        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                reviewAndPaySenderInfo: model
            });

            props.handleSenderInfoSubmit(model);
        }

        setStepsStatus({
            ...stepsStatus,
            senderInfo: isModelValid
        });
    };

    //#endregion

    //#region WHEN TO SEND STEP

    const handleWhenToSendSubmit = (model: IReviewAndPayWhenToSendStepModel): void => {
        const isModelValid = ReviewAndPayWhenToSendStepModel.isValid(model);
        
        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                reviewAndPayWhenToSend: model
            });

            props.handleWhenToSendSubmit(model);
        }

        setStepsStatus({
            ...stepsStatus,
            whenToSend: isModelValid
        });
    };

    //#endregion

    //#region PAYMENT STEP

    const handleError = (message: string): void => {
        props.handleError(message);
    };

    const handlePaymentSubmit = (model: IReviewAndPayPaymentStepModel): void => {
        const isModelValid = ReviewAndPayPaymentStepModel.isValid(model);
        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                reviewAndPayPayment: model
            });
        }

        setStepsStatus({
            ...stepsStatus,
            paymentStep: isModelValid
        });
    };

    //#endregion

    //#region COMMON HELPERS

    const handleSetActiveStep = (newState: BuyGiftCardStateEnum): void => {
        props.handleStateChange(BuyGiftCardSubpageEnum.REVIEW_PAY, newState);
    };

    const handleStateChange = (newSubpage: BuyGiftCardSubpageEnum, newState: BuyGiftCardStateEnum): void => {
        props.handleStateChange(newSubpage, newState);
    };

    //#endregion

    return (
        <div className="review-and-pay">
            <ReviewAndPayReviewStep
                stepModel={props.reviewModel}
                isSaving={isSaving}
                activeStep={props.activeStep}

                handleSetActiveStep={handleSetActiveStep}
            />

            <ReviewAndPaySenderInfoStep
                stepModel={subpageModel.reviewAndPaySenderInfo}
                isSaving={isSaving}
                activeStep={props.activeStep}

                handleSetActiveStep={handleSetActiveStep}
                handleSubmit={handleSenderInfoSubmit}
            />

            <ReviewAndPayWhenToSendStep
                stepModel={subpageModel.reviewAndPayWhenToSend}
                isSaving={isSaving}
                activeStep={props.activeStep}
                isAvailable={stepsStatus.senderInfo}

                handleSetActiveStep={handleSetActiveStep}
                handleSubmit={handleWhenToSendSubmit}
            />

            <ReviewAndPayPaymentStep
                stepModel={subpageModel.reviewAndPayPayment}
                isSaving={isSaving}
                activeStep={props.activeStep}
                isAvailable={stepsStatus.senderInfo && stepsStatus.whenToSend && !props.hasToSaveBasket}
                paymentDetails={{
                    totalAmount: props.reviewModel.totalAmount,
                    senderInfo: {
                        isBusinessBuyer: subpageModel.reviewAndPaySenderInfo?.useBusinessDetails ?? false,
                        fullName: subpageModel.reviewAndPaySenderInfo.usePersonalDetails ? subpageModel.reviewAndPaySenderInfo.personDetails?.fullName ?? ""
                            : subpageModel.reviewAndPaySenderInfo.useBusinessDetails ? subpageModel.reviewAndPaySenderInfo.businessDetails?.fullName ?? "" : "",
                        email: subpageModel.reviewAndPaySenderInfo.usePersonalDetails ? subpageModel.reviewAndPaySenderInfo.personDetails?.email ?? ""
                            : subpageModel.reviewAndPaySenderInfo.useBusinessDetails ? subpageModel.reviewAndPaySenderInfo.businessDetails?.email ?? "" : "",
                        companyName: subpageModel.reviewAndPaySenderInfo.businessDetails?.companyName ?? ""
                    }
                }}

                getPageModel={props.getPageModel}
                handleError={handleError}
                handleSetActiveStep={handleSetActiveStep}
                handleStateChange={handleStateChange}
                handleSubmit={handlePaymentSubmit}
            />
        </div>
    );
};

export default ReviewAndPaySubpage;